import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Globe } from "lucide-react";
import { useLang } from "@/context/LangContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_secure-certified/artifacts/l22oi3ly_gaca-logo%20%281%29.svg";

function useScrollState() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [setScrolled]);
  return scrolled;
}

function DesktopNav({ navLinks, activeDropdown, setActiveDropdown, location }) {
  return (
    <>
      {navLinks.map((link) => (
        <div
          key={link.href}
          className="relative group"
          onMouseEnter={() => link.children && setActiveDropdown(link.label)}
          onMouseLeave={() => setActiveDropdown(null)}
        >
          <Link
            to={link.href}
            data-testid={`nav-link-${link.href.slice(1) || 'home'}`}
            className={`flex items-center gap-1 px-3 py-2 text-[13px] font-medium tracking-wide uppercase transition-colors duration-200 ${
              location.pathname === link.href ? "text-[#13A8BD]" : "text-[#0B2545] hover:text-[#13A8BD]"
            }`}
          >
            {link.label}
            {link.children && <ChevronDown size={12} className="opacity-50" />}
          </Link>
          {link.children && activeDropdown === link.label && (
            <div className="absolute top-full left-0 w-64 bg-white border border-[#0B2545]/10 shadow-lg animate-fade-in z-50">
              {link.children.map((child) => (
                <Link
                  key={`${child.href}-${child.label}`}
                  to={child.href}
                  className="block px-5 py-3 text-sm text-[#0B2545] hover:bg-[#F5F7FA] hover:text-[#13A8BD] transition-colors border-b border-[#0B2545]/5 last:border-b-0"
                >
                  {child.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      ))}
    </>
  );
}

function LanguageSwitcher({ lang, setLang, LANGS }) {
  const [langOpen, setLangOpen] = useState(false);
  return (
    <div className="relative ml-2" onMouseEnter={() => setLangOpen(true)} onMouseLeave={() => setLangOpen(false)}>
      <button data-testid="lang-switcher" className="flex items-center gap-1 px-2 py-2 text-[12px] font-bold text-[#0B2545] uppercase tracking-wider hover:text-[#13A8BD] transition-colors">
        <Globe size={14} /> {lang.toUpperCase()}
      </button>
      {langOpen && (
        <div className="absolute top-full right-0 w-40 bg-white border border-[#0B2545]/10 shadow-lg z-50 animate-fade-in">
          {LANGS.map((l) => (
            <button key={l.code} onClick={() => { setLang(l.code); setLangOpen(false); }} data-testid={`lang-${l.code}`}
              className={`w-full text-left px-4 py-2.5 text-xs hover:bg-[#F7F9FC] transition-colors border-b border-[#0B2545]/5 last:border-b-0 ${lang === l.code ? 'text-[#13A8BD] font-bold' : 'text-[#0B2545]'}`}>
              <span className="font-bold mr-2">{l.label}</span> {l.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MobileNav({ navLinks, location, lang, setLang, LANGS, t }) {
  return (
    <div data-testid="mobile-nav" className="lg:hidden bg-white border-t border-[#0B2545]/10 animate-fade-in max-h-[80vh] overflow-y-auto">
      <div className="px-6 py-4 space-y-1">
        {navLinks.map((link) => (
          <div key={link.href}>
            <Link
              to={link.href}
              data-testid={`mobile-nav-link-${link.href.slice(1) || 'home'}`}
              className={`block text-sm font-medium uppercase tracking-wider py-3 border-b border-[#0B2545]/5 ${
                location.pathname === link.href ? "text-[#13A8BD]" : "text-[#0B2545]"
              }`}
            >
              {link.label}
            </Link>
          </div>
        ))}
        <div className="flex gap-2 pt-3 pb-2">
          {LANGS.map((l) => (
            <button key={l.code} onClick={() => setLang(l.code)} data-testid={`mobile-lang-${l.code}`}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border ${lang === l.code ? 'bg-[#13A8BD] text-white border-[#13A8BD]' : 'border-[#0B2545]/15 text-[#0B2545]'}`}>
              {l.label}
            </button>
          ))}
        </div>
        <Link to="/contact" data-testid="mobile-nav-cta"
          className="block bg-[#13A8BD] text-white text-center px-6 py-3 text-sm font-semibold uppercase tracking-wider mt-4">
          {t?.nav?.apply || "Apply for Certification"}
        </Link>
      </div>
    </div>
  );
}

export default function Navbar() {
  const scrolled = useScrollState();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const { lang, setLang, t, LANGS } = useLang();

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [setMobileOpen, setActiveDropdown]);

  useEffect(() => {
    closeMobile();
  }, [location, closeMobile]);

  const navLinks = [
    {
      label: t?.nav?.services || "Services",
      href: "/services",
      children: [
        { label: "ISO/IEC 27001", href: "/services" },
        { label: "ISO 9001", href: "/services" },
        { label: "ISO 22301", href: "/services" },
        { label: "ISO 27701", href: "/services" },
      ],
    },
    {
      label: t?.nav?.certification || "Certification",
      href: "/process",
      children: [
        { label: t?.nav?.process || "Certification Process", href: "/process" },
        { label: t?.nav?.accreditations || "Accreditations", href: "/accreditations" },
        { label: t?.nav?.why_gaca || "Why GACA", href: "/why-gaca" },
      ],
    },
    { label: t?.nav?.industries || "Industries", href: "/industries" },
    { label: t?.nav?.training || "Training", href: "/training" },
    {
      label: t?.nav?.about || "About",
      href: "/about",
      children: [
        { label: t?.nav?.about || "About GACA", href: "/about" },
        { label: t?.nav?.insights || "Insights", href: "/insights" },
        { label: t?.nav?.careers || "Careers", href: "/careers" },
        { label: t?.nav?.faq || "FAQ", href: "/faq" },
      ],
    },
    { label: t?.nav?.contact || "Contact", href: "/contact" },
  ];

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/98 backdrop-blur-sm shadow-sm" : "bg-white"
      }`}
    >
      <div className="h-1 bg-[#13A8BD]" />
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 flex-shrink-0">
            <img src={LOGO_URL} alt="GACA International" className="h-16 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            <DesktopNav navLinks={navLinks} activeDropdown={activeDropdown} setActiveDropdown={setActiveDropdown} location={location} />
            <Link
              to="/contact"
              data-testid="nav-cta-button"
              className="ml-2 bg-[#13A8BD] text-white px-5 py-2.5 text-[13px] font-semibold uppercase tracking-wider hover:bg-[#0e8a9c] transition-colors duration-200"
            >
              {t?.nav?.apply || "Apply for Certification"}
            </Link>
            <LanguageSwitcher lang={lang} setLang={setLang} LANGS={LANGS} />
          </nav>

          <button data-testid="mobile-menu-toggle" className="lg:hidden p-2 text-[#0B2545]" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {mobileOpen && <MobileNav navLinks={navLinks} location={location} lang={lang} setLang={setLang} LANGS={LANGS} t={t} />}
    </header>
  );
}
